/**
 * Login — Sep 2026 · r-login-magic
 *
 * A more alive, more excited login experience:
 *   · Rotating hero images (from platform branding.login_hero_images
 *     when configured, otherwise a curated Unsplash set of "team at
 *     work" photos). Cross-fade every 8s.
 *   · A rotating motivational line — either the admin-authored
 *     `login_quotes` or bundled defaults. Refreshes every 12s with a
 *     subtle slide-in.
 *   · A live "AI Agent activity" strip that cycles through the agents
 *     currently monitoring the workspace ("Compliance Agent: sweeping
 *     yesterday's timesheets", "HR Agent: preparing today's digest",
 *     …). Gives the immediate feeling that the system is alive and
 *     already working before the user has even signed in.
 *   · Subtle grain + diagonal stripes retained from the previous
 *     design, plus a soft primary-tinted vignette so the pane feels
 *     branded rather than a stock photo overlay.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Sparkles } from "lucide-react";

// Default hero images (used when platform admin hasn't uploaded any).
// Chosen for a "modern workplace" feel without stock-photo cheese. All
// Unsplash CDN so they load fast and never 404 on our side.
const DEFAULT_HERO_IMAGES = [
  "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1600&q=70",
  "https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=1600&q=70",
  "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=70",
  "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1600&q=70",
  "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1600&q=70",
];

const DEFAULT_QUOTES = [
  "Small ships steer fast — you're the reason this workspace hums today.",
  "Ship the boring stuff. The magic follows on its own.",
  "The best hour of the day is the one you didn't waste on friction.",
  "Progress compounds. Log the little wins.",
  "Your future self will thank you for the notes you leave today.",
  "Momentum > motivation. Take the next tiny step.",
  "Great teams look ordinary from the outside. That's the point.",
  "A clean inbox is a mediocre goal. Do the work that changes the number.",
  "Move at the speed of trust. Faster only breaks things.",
  "The scoreboard tells the truth. Check it, then get back to shipping.",
];

// Agent activity ticker — keeps changing so returning users always see
// something new; the exact wording is fake-live but honest about what
// each agent actually does inside the app.
const AGENT_LINES = [
  { label: "Compliance Agent", body: "sweeping yesterday's timesheets" },
  { label: "HR Agent", body: "drafting today's leave digest" },
  { label: "PM Agent", body: "watching 42 open deadlines" },
  { label: "Comms Agent", body: "grouping 18 fresh client replies" },
  { label: "Finance Agent", body: "reconciling last night's invoices" },
  { label: "Compliance Agent", body: "warming up the daily monitor" },
  { label: "Goals Agent", body: "syncing this week's OKR check-ins" },
  { label: "PM Agent", body: "auto-triaging 6 overdue tasks" },
];

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const { branding } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // ── Rotating hero images ────────────────────────────────────────
  const heroImages = useMemo(() => {
    const custom = Array.isArray(branding?.login_hero_images)
      ? branding.login_hero_images.filter((s) => typeof s === "string" && s.trim())
      : [];
    return custom.length > 0 ? custom : DEFAULT_HERO_IMAGES;
  }, [branding?.login_hero_images]);
  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    if (heroImages.length < 2) return;
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % heroImages.length), 8000);
    return () => clearInterval(t);
  }, [heroImages.length]);

  // ── Rotating motivational quote ──────────────────────────────────
  const quotes = useMemo(() => {
    const custom = Array.isArray(branding?.login_quotes)
      ? branding.login_quotes.filter((s) => typeof s === "string" && s.trim())
      : [];
    return custom.length > 0 ? custom : DEFAULT_QUOTES;
  }, [branding?.login_quotes]);
  const [quoteIdx, setQuoteIdx] = useState(() => Math.floor(Math.random() * DEFAULT_QUOTES.length));
  useEffect(() => {
    if (quotes.length < 2) return;
    const t = setInterval(() => setQuoteIdx((i) => (i + 1) % quotes.length), 12000);
    return () => clearInterval(t);
  }, [quotes.length]);

  // ── Live agent ticker ────────────────────────────────────────────
  const [agentIdx, setAgentIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setAgentIdx((i) => (i + 1) % AGENT_LINES.length), 3500);
    return () => clearInterval(t);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      let dest = loc.state?.from || "/app";
      if (!u.is_super_admin) {
        try {
          const last = localStorage.getItem("acq_last_page");
          if (last && last.startsWith("/app") && last !== "/app/") {
            dest = last;
          }
        } catch { /* ignore */ }
        localStorage.removeItem("acq_last_page");
      }
      nav(u.is_super_admin ? "/super-admin" : dest);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Login failed"));
    } finally {
      setBusy(false);
    }
  };

  const currentAgent = AGENT_LINES[agentIdx];
  const currentQuote = quotes[quoteIdx % quotes.length];

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-background">
      {/* ── Left: cinematic hero ────────────────────────────────── */}
      <div className="hidden lg:block relative overflow-hidden bg-foreground text-background">
        {/* Cross-fading image stack */}
        <div className="absolute inset-0" aria-hidden="true">
          {heroImages.map((src, i) => (
            <img
              key={src + i}
              src={src}
              alt=""
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1600ms] ease-in-out"
              style={{ opacity: i === heroIdx ? 0.55 : 0 }}
              draggable={false}
            />
          ))}
        </div>
        {/* Vignette + brand-tint overlay + grain + stripes so the images
             never look like a raw stock photo. */}
        <div
          className="absolute inset-0"
          aria-hidden="true"
          style={{
            background:
              "linear-gradient(180deg, hsl(0 0% 0% / 0.55) 0%, hsl(0 0% 0% / 0.35) 40%, hsl(0 0% 0% / 0.85) 100%), " +
              "radial-gradient(120% 80% at 80% 0%, hsl(var(--primary) / 0.35) 0%, transparent 60%)",
          }}
        />
        <div className="absolute inset-0 grain opacity-25" />
        <div className="absolute inset-0 diag-stripes opacity-40" />

        {/* Brand mark top-left */}
        <Link to="/" className="absolute top-8 left-10 flex items-center gap-2 z-10">
          <div className="w-8 h-8 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-black overflow-hidden">
            {branding.logo_icon_url ? (
              <img src={branding.logo_icon_url} alt={branding.platform_name} className="w-full h-full object-cover" />
            ) : branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.platform_name} className="w-full h-full object-cover" />
            ) : (
              (branding.platform_name || "A").slice(0, 1)
            )}
          </div>
          <span className="font-bold tracking-tight flex items-baseline gap-0.5">
            {branding.platform_name}
            <sup className="text-[0.5em] text-background/60 font-normal" aria-hidden="true">™</sup>
          </span>
        </Link>

        {/* Live agent activity strip — top-right, small, but very
             present. Green pulsing dot on the left keeps the eye. */}
        <div
          className="absolute top-8 right-10 z-10 flex items-center gap-2 border border-background/25 bg-background/10 backdrop-blur-md px-3 py-1.5 text-xs"
          data-testid="login-agent-ticker"
          title="Live AI agents monitoring your workspace"
        >
          <span className="relative flex items-center justify-center">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            <span className="absolute w-3 h-3 rounded-full bg-emerald-400/40 animate-ping" />
          </span>
          <span className="font-semibold text-background/90">{currentAgent.label}</span>
          <span className="text-background/60">·</span>
          <span key={agentIdx} className="text-background/80 login-fade-in">{currentAgent.body}</span>
        </div>

        {/* Bottom copy — quote + tagline */}
        <div className="absolute bottom-12 left-10 right-10 z-10">
          <div className="label-eyebrow opacity-60 mb-4 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> {branding.platform_name}<sup className="text-[0.55em] opacity-70" aria-hidden="true">™</sup> · AI ops for MSMEs
          </div>
          <h2 className="heading-display text-4xl xl:text-5xl 2xl:text-6xl text-background leading-[1.05]">
            Where great teams<br />
            get sharper<br />
            every day.
          </h2>
          <p className="mt-5 max-w-md text-base text-background/70">
            Sharper decisions. Fewer surprises. AI agents that never sleep — quietly running compliance, HR, projects and comms so your team can do the work that actually moves the number.
          </p>
          <div
            key={quoteIdx}
            className="mt-6 max-w-md text-sm text-background/80 login-fade-in"
            data-testid="login-quote"
          >
            <span className="text-background/50 mr-1">“</span>
            {currentQuote}
            <span className="text-background/50 ml-1">”</span>
          </div>
        </div>
      </div>

      {/* ── Right: form ─────────────────────────────────────────── */}
      <form onSubmit={submit} className="flex flex-col justify-center px-6 sm:px-10 lg:px-20 py-16 max-w-xl w-full mx-auto">
        <Link to="/" className="lg:hidden mb-10 inline-flex items-center gap-2">
          <div className="w-7 h-7 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-black overflow-hidden">
            {branding.logo_icon_url ? (
              <img src={branding.logo_icon_url} alt={branding.platform_name} className="w-full h-full object-cover" />
            ) : (
              (branding.platform_name || "A").slice(0, 1)
            )}
          </div>
          <span className="font-bold flex items-baseline gap-0.5">
            {branding.platform_name}
            <sup className="text-[0.5em] text-muted-foreground font-normal" aria-hidden="true">™</sup>
          </span>
        </Link>

        {/* Mobile-only agent ticker so the "AI is alive" feel isn't lost
             on phones where the hero pane is hidden. */}
        <div
          className="lg:hidden flex items-center gap-2 border border-border px-2 py-1 text-[11px] mb-6 self-start"
          data-testid="login-agent-ticker-mobile"
        >
          <span className="relative flex items-center justify-center">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span className="absolute w-3 h-3 rounded-full bg-emerald-500/30 animate-ping" />
          </span>
          <span className="font-semibold">{currentAgent.label}</span>
          <span className="text-muted-foreground">·</span>
          <span key={agentIdx} className="login-fade-in">{currentAgent.body}</span>
        </div>

        <div className="label-eyebrow mb-2">Welcome back</div>
        <h1 className="heading-display text-4xl mb-2 flex items-baseline gap-1">
          Log in to {branding.platform_name}
          <sup className="text-[0.4em] text-muted-foreground font-normal" aria-hidden="true">™</sup>
          <span>.</span>
        </h1>
        <p className="text-sm text-muted-foreground mb-10">Your agents have been busy — sign in to see what changed.</p>

        <div className="space-y-5">
          <div>
            <Label htmlFor="email" className="text-xs uppercase tracking-widest font-mono mb-2 inline-block">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="login-email-input" />
          </div>
          <div>
            <Label htmlFor="pw" className="text-xs uppercase tracking-widest font-mono mb-2 inline-block">Password</Label>
            <Input id="pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" data-testid="login-password-input" />
          </div>
          <Button type="submit" className="w-full h-11 rounded-none font-bold uppercase tracking-widest text-xs" disabled={busy} data-testid="login-submit-btn">
            {busy ? "Logging in…" : "Log in"}
          </Button>
        </div>

        <p className="text-sm text-muted-foreground mt-6">
          No account?{" "}
          <Link to="/signup" className="underline text-foreground" data-testid="login-signup-link">
            Create one
          </Link>
          <span className="mx-2 text-foreground/30">·</span>
          <Link to="/forgot-password" className="underline text-foreground" data-testid="login-forgot-link">
            Forgot password?
          </Link>
        </p>

        {/* Quote for mobile users (hero is hidden on lg:below) */}
        <div
          key={`m-${quoteIdx}`}
          className="lg:hidden mt-8 border-l-2 border-primary pl-3 text-xs text-muted-foreground login-fade-in"
          data-testid="login-quote-mobile"
        >
          <span className="text-foreground/40 mr-1">“</span>
          {currentQuote}
          <span className="text-foreground/40 ml-1">”</span>
        </div>
      </form>
    </div>
  );
}
